import {
  asArray,
  asBoolean,
  asNullableNumber,
  asNumber,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import { normalizeInvoiceCoverage, normalizeRemainderLines } from './invoiceCoverage';
import { normalizeComplaintLines } from './workflowInvoice';
import type { InvoiceCoverage, InvoiceCoverageLine } from '../types/invoiceCoverage';
import type { WorkflowInvoiceComplaintLine } from '../types/workflowInvoice';

/**
 * WP2 (docs/decisions/2026-09-24-wp2-korekty-faktur.md): corrections and
 * withdrawal of one approved invoice — GET …/:id/corrections. Amounts are
 * PLN net; `null` means unknown (never zero).
 */
export interface InvoiceCorrectionRow {
  id: string;
  number: string | null;
  status: 'DRAFT' | 'APPROVED' | 'CANCELLED';
  recognitionMonth: string | null;
  netPLN: number | null;
  approvedAt: string | null;
  /** WP15: „Reklamacja” with the complained quantity per order line. */
  reason: 'COMPLAINT' | null;
  complaintLines: WorkflowInvoiceComplaintLine[];
}

export interface InvoiceWithdrawalInfo {
  id: string;
  reason: string;
  recognitionMonth: string;
  withdrawnAt: string | null;
  withdrawnByName: string | null;
}

export interface InvoiceCorrectionState {
  invoiceId: string;
  isCorrection: boolean;
  original: { id: string; number: string | null; recognitionMonth: string | null } | null;
  orderId: string | null;
  /** The original still recognizes money (approved, not withdrawn). */
  active: boolean;
  withdrawal: InvoiceWithdrawalInfo | null;
  corrections: InvoiceCorrectionRow[];
  originalNetPLN: number | null;
  effectiveNetPLN: number | null;
  effectiveInvoiceId: string | null;
  /** WP11: niezafakturowana reszta — lines with an uninvoiced remainder (quantities only). */
  uninvoicedLines: InvoiceCoverageLine[];
  /** WP11 coverage of the whole ZK/ZD (order lines with names and units); null = unknown. */
  invoiceCoverage: InvoiceCoverage | null;
  /** Sales: stock issued beyond the invoiced quantity (cost outside the settlement). */
  issuedOverInvoice: Array<{ orderProductId: string; name: string; unit: string; quantity: number }>;
  openResettlementEntries: number;
  canCorrect: boolean;
  canWithdraw: boolean;
  /** YYYY-MM the withdrawal dialog offers („Wycofaj w MM.RRRR”); null = no suggestion (backend rule). */
  withdrawalMonthSuggestion: string | null;
}

const STATUSES = new Set(['DRAFT', 'APPROVED', 'CANCELLED']);

export function normalizeInvoiceCorrectionState(raw: unknown, entity = 'invoices.corrections'): InvoiceCorrectionState {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);
  const original = asRecord(record?.original);
  const withdrawal = asRecord(record?.withdrawal);
  return {
    invoiceId: asString(record?.invoiceId),
    isCorrection: asBoolean(record?.isCorrection),
    original: original ? {
      id: asString(original.id),
      number: asOptionalString(original.number) ?? null,
      recognitionMonth: asOptionalString(original.recognitionMonth) ?? null,
    } : null,
    orderId: asOptionalString(record?.orderId) ?? null,
    active: asBoolean(record?.active),
    withdrawal: withdrawal ? {
      id: asString(withdrawal.id),
      reason: asString(withdrawal.reason),
      recognitionMonth: asString(withdrawal.recognitionMonth),
      withdrawnAt: asOptionalString(withdrawal.withdrawnAt) ?? null,
      withdrawnByName: asOptionalString(withdrawal.withdrawnByName) ?? null,
    } : null,
    corrections: asArray(record?.corrections, (item, index) => {
      const row = asRecord(item);
      const status = asString(row?.status);
      if (!STATUSES.has(status)) reportDtoAnomaly(`${entity}.corrections[${index}]`, 'status', item);
      return {
        id: asString(row?.id),
        number: asOptionalString(row?.number) ?? null,
        status: (STATUSES.has(status) ? status : 'DRAFT') as InvoiceCorrectionRow['status'],
        recognitionMonth: asOptionalString(row?.recognitionMonth) ?? null,
        netPLN: asNullableNumber(row?.netPLN),
        approvedAt: asOptionalString(row?.approvedAt) ?? null,
        reason: row?.reason === 'COMPLAINT' ? 'COMPLAINT' : null,
        complaintLines: normalizeComplaintLines(row?.complaintLines),
      };
    }),
    originalNetPLN: asNullableNumber(record?.originalNetPLN),
    effectiveNetPLN: asNullableNumber(record?.effectiveNetPLN),
    effectiveInvoiceId: asOptionalString(record?.effectiveInvoiceId) ?? null,
    uninvoicedLines: normalizeRemainderLines(record?.uninvoicedLines),
    invoiceCoverage: normalizeInvoiceCoverage(record?.invoiceCoverage, `${entity}.invoiceCoverage`),
    issuedOverInvoice: asArray(record?.issuedOverInvoice, (item) => {
      const line = asRecord(item);
      return { orderProductId: asString(line?.orderProductId), name: asString(line?.name), unit: asString(line?.unit), quantity: asNumber(line?.quantity) };
    }),
    openResettlementEntries: asNumber(record?.openResettlementEntries),
    canCorrect: asBoolean(record?.canCorrect),
    canWithdraw: asBoolean(record?.canWithdraw),
    withdrawalMonthSuggestion: typeof record?.withdrawalMonthSuggestion === 'string' ? record.withdrawalMonthSuggestion : null,
  };
}
